import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Key, Lock, Check, ChevronDown, Search, Shield } from 'lucide-react';
import { usePermissions } from '@/store/permission';
import { useResources } from '@/store/resource';

import { PageHeader } from '@/components/ui/PageHeader';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { InputField } from '@/components/ui/InputField';
import { useLayout } from '@/contexts/layout-context';

export const CreatePermissionPage = () => {
    const [key, setKey] = useState('');
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [resourceKindId, setResourceKindId] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const createPermission = usePermissions((state) => state.create);
    const { resourceKinds, listResourceKinds } = useResources();
    const navigate = useNavigate();
    const { setBreadcrumbs } = useLayout();

    useEffect(() => {
        setBreadcrumbs([{ name: 'Permissions', to: '/m/permissions' }, { name: 'Create' }]);
        listResourceKinds().catch(console.error);
    }, [setBreadcrumbs, listResourceKinds]);

    useEffect(() => {
        const handler = (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            if (!target.closest('.searchable-select-container')) {
                setIsOpen(false);
                setSearchQuery('');
            }
        };
        document.addEventListener('click', handler);
        return () => document.removeEventListener('click', handler);
    }, []);

    const submit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!resourceKindId) {
            toast.error('Please select a Resource Kind');
            return;
        }
        if (!key) {
            toast.error('Please enter a Permission Key');
            return;
        }
        if (!name) {
            toast.error('Please enter a Permission Name');
            return;
        }
        setIsSubmitting(true);
        try {
            const fullPermissionId = `${resourceKindId}::${key}`;
            await createPermission({
                id: fullPermissionId,
                name,
                description,
                resource_kind_id: resourceKindId,
            } as any);
            toast.success('Permission defined successfully');
            navigate('/m/permissions');
        } catch {
            toast.error('Failed to create permission');
        } finally {
            setIsSubmitting(false);
        }
    };

    const selectedKind = resourceKinds.find(k => k.id === resourceKindId);
    const displayValue = isOpen ? searchQuery : (selectedKind ? `${selectedKind.name} (${selectedKind.id})` : '');
    const filteredKinds = resourceKinds.filter(k =>
        k.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        k.id.toLowerCase().includes(searchQuery.toLowerCase())
    );

    return (
        <div className="p-4 lg:p-8 max-w-4xl mx-auto w-full animate-in fade-in duration-500">
            <PageHeader
                title="Create Permission"
                subtitle="Define a new fine-grained entitlement to be used within roles and resource kinds."
                actions={
                    <Button variant="ghost" onClick={() => navigate('/m/permissions')} className="gap-2">
                        <ArrowLeft className="w-4 h-4" />
                        Back
                    </Button>
                }
            />

            <div className="mt-8 animate-in slide-in-from-bottom-4 duration-500">
                <Card
                    title="Permission Identity"
                    subtitle="Configure the system name and description for this access scope."
                    className="glass"
                >
                    <form onSubmit={submit} className="space-y-8">
                        {/* 1. Resource Kind Selection at the top */}
                        <div className="space-y-2 relative searchable-select-container">
                            <label className="block text-sm font-bold text-slate-700 dark:text-slate-300">Resource Kind <span className="text-red-500">*</span></label>
                            <div className="relative">
                                <input
                                    type="text"
                                    className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg pl-10 pr-10 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 dark:focus:ring-orange-500 transition-all font-medium text-slate-900 dark:text-white w-full cursor-pointer"
                                    placeholder="Select resource kind..."
                                    value={displayValue}
                                    onChange={(e) => {
                                        setSearchQuery(e.target.value);
                                        if (!isOpen) setIsOpen(true);
                                    }}
                                    onFocus={() => setIsOpen(true)}
                                />
                                <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400">
                                    <Search className="w-4 h-4" />
                                </div>
                                <button
                                    type="button"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setIsOpen(!isOpen);
                                    }}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
                                >
                                    <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
                                </button>
                            </div>

                            {isOpen && (
                                <div className="absolute z-50 left-0 right-0 mt-1 bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-lg shadow-xl max-h-60 overflow-y-auto divide-y divide-slate-100 dark:divide-slate-900">
                                    {filteredKinds.length > 0 ? (
                                        filteredKinds.map((k) => (
                                            <div
                                                key={k.id}
                                                onClick={() => {
                                                    setResourceKindId(k.id);
                                                    setIsOpen(false);
                                                    setSearchQuery('');
                                                }}
                                                className={`px-4 py-2.5 text-sm cursor-pointer hover:bg-orange-50 dark:hover:bg-orange-955/20 hover:text-orange-600 transition-colors flex flex-col ${resourceKindId === k.id ? 'bg-orange-50/50 dark:bg-orange-955/10 text-orange-600 font-medium' : 'text-slate-700 dark:text-slate-300'
                                                    }`}
                                            >
                                                <span className="font-semibold text-slate-900 dark:text-white">{k.name}</span>
                                                <span className="text-[10px] text-slate-400 dark:text-slate-500 mt-0.5">{k.id}</span>
                                            </div>
                                        ))
                                    ) : (
                                        <div className="px-4 py-3 text-sm text-slate-400 dark:text-slate-500 text-center">
                                            No resource kinds match your search
                                        </div>
                                    )}
                                </div>
                            )}
                            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Associate this permission with a specific type of resource kind.</p>
                        </div>

                        {/* 2. Key, Name and Description below it, disabled until Resource Kind is selected */}
                        <div className="space-y-6">
                            <div className="flex flex-col gap-1.5">
                                <label className="text-sm font-medium text-slate-700 dark:text-slate-300">
                                    Permission Key <span className="text-red-500">*</span>
                                </label>
                                <div className={`flex rounded-lg border overflow-hidden transition-all duration-200 ${!resourceKindId
                                    ? 'border-slate-100 dark:border-slate-800/50 bg-slate-50/50 dark:bg-slate-950/20'
                                    : 'border-slate-200 dark:border-slate-700 focus-within:border-orange-500 focus-within:ring-4 focus-within:ring-orange-500/10'
                                    }`}>
                                    {resourceKindId && (
                                        <span className="bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 border-r border-slate-200 dark:border-slate-700 px-3 py-2 text-sm flex items-center font-mono select-none">
                                            {resourceKindId}::
                                        </span>
                                    )}
                                    <div className="relative flex-1 flex items-center">
                                        <div className="absolute left-3 text-slate-400">
                                            <Key className="w-4 h-4" />
                                        </div>
                                        <input
                                            type="text"
                                            className="w-full pl-10 pr-4 py-2 bg-transparent text-sm outline-none dark:text-white dark:placeholder-slate-500 disabled:text-slate-400 cursor-text"
                                            placeholder={resourceKindId ? "e.g. read" : "Select Resource Kind first..."}
                                            value={key}
                                            onChange={(e) => setKey(e.target.value)}
                                            required
                                            disabled={!resourceKindId}
                                        />
                                    </div>
                                </div>
                                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                                    {!resourceKindId
                                        ? "Please select a Resource Kind first."
                                        : "Enter the operation key (typically lowercase, e.g. read, write, delete)."
                                    }
                                </p>
                            </div>

                            <InputField
                                label="Permission Name"
                                placeholder={resourceKindId ? "e.g. Read Users" : "Select Resource Kind first..."}
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                icon={<Shield className="w-4 h-4" />}
                                disabled={!resourceKindId}
                                required
                            />

                            <InputField
                                label="Brief Description"
                                placeholder={resourceKindId ? "What does this permission grant?" : "Select Resource Kind first..."}
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                icon={<Lock className="w-4 h-4" />}
                                disabled={!resourceKindId}
                            />
                        </div>

                        <div className="pt-8 border-t border-slate-100 dark:border-slate-800 flex justify-end gap-3">
                            <Button variant="ghost" type="button" onClick={() => navigate('/m/permissions')}>
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                isLoading={isSubmitting}
                                disabled={!resourceKindId || !key || !name}
                                className="min-w-40 gap-2"
                            >
                                <Check className="w-4 h-4" />
                                Define Permission
                            </Button>
                        </div>
                    </form>
                </Card>
            </div>
        </div>
    );
};
